import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../../../core/services/auth/user/user.service';
import { User } from '../../../core/services/auth/user/user.interface';
import { BrokerProfileService } from '../../../core/services/auth/broker-profile/broker-profile.service';
import { Profile } from '../../../core/services/auth/profile/profile.interface';
import { ProfileSwitchDialogComponent } from './profile-switch-dialog.component';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User = new User({});
    displayProfile: Profile | null = null;
    canSwitchProfile: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _dialog: MatDialog,
        private _userService: UserService,
        private _brokerProfileService: BrokerProfileService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Combiner les deux observables pour déterminer quel profil afficher
        combineLatest([
            this._userService.user$,
            this._brokerProfileService.selectedProfile$
        ])
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(([user, brokerProfile]) => {
            this.user = user;

            // Logique de sélection du profil à afficher
            if (brokerProfile) {
                // Cas 1: Broker avec profil sélectionné
                this.displayProfile = brokerProfile;
            } else if (user.profiles && user.profiles.length === 1) {
                // Cas 2: Non-broker avec exactement 1 profil
                this.displayProfile = user.profiles[0];
            } else {
                // Cas 3: Aucun profil ou multiple profils
                this.displayProfile = null;
            }

            // Vérifier si l'utilisateur peut changer de profil
            const switchableTypes = ['BROKER_POINT_OF_SALE', 'CONTRIBUTOR', 'SUB_CONTRIBUTOR'];
            this.canSwitchProfile = switchableTypes.includes(user.entityAccessLevel);

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    

    /**
     * Navigate to the user's profile page
     */
    navigateToProfile(): void {
        this._router.navigate(['/my-profile']);
    }

    /**
     * Open profile switch dialog
     */
    openProfileSwitch(): void {
        this._dialog.open(ProfileSwitchDialogComponent, {
            width: '480px',
            maxHeight: '80vh'
        });
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }
}
